/* eslint-disable no-console */
import React, { useCallback, useEffect, useRef } from 'react';
import { useLatest } from 'ahooks';
import { SocketClientType } from '@/service/socketio';
import service from '@/service';

interface Props {
  socketInstance?: React.MutableRefObject<SocketClientType>;
  videoRef: React.MutableRefObject<HTMLVideoElement>;
  rtcAPIType: RTCAPIType;
  onRTCConnectionStateChange?: (state: RTCPeerConnectionState) => void;
  onVideoPlay?: VoidFunc;
  deviceId: string;
}

export type RTCAPIType = 'order-service' | 'auxiliary-position';
export const useWebRtc = (props: Props) => {
  const { videoRef, rtcAPIType, onRTCConnectionStateChange, onVideoPlay, socketInstance, deviceId } = props;
  const latestDeviceId = useLatest(deviceId);
  const isUnMount = useRef(false);
  const currentConnectState = useRef<RTCPeerConnectionState>(null);
  const isStartedOpenRTC = useRef(false);

  const pcRef = useRef<RTCPeerConnection>(null);

  // 监听RTC连接状态
  const onConnectionstatechange = useCallback(
    event => {
      console.log('[WebRTC] onConnectionstatechange:', event.currentTarget?.connectionState);
      if (isUnMount.current) return;
      const state = (event.currentTarget as RTCPeerConnection)?.connectionState;
      currentConnectState.current = state;
      if (state === 'failed') {
        videoRef.current?.removeEventListener('play', onVideoPlayEvent);
        stop();
      }
      if (['closed', 'failed', 'disconnected'].includes(state)) {
        isStartedOpenRTC.current = false;
      }
      onRTCConnectionStateChange?.(state);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onVideoPlayEvent = useCallback(() => {
    onVideoPlay?.();
  }, [onVideoPlay]);

  useEffect(() => {
    let pcRefPersist = pcRef.current;

    return () => {
      isUnMount.current = true;
      pcRefPersist?.removeEventListener('connectionstatechange', onConnectionstatechange);
      pcRefPersist = null;
      pcRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createOffer = useCallback(() => {
    return pcRef.current
      .createOffer()
      .then(offer => {
        return pcRef.current.setLocalDescription(offer);
      })
      .then(() => {
        // wait for ICE gathering to complete
        return new Promise(resolve => {
          if (pcRef.current?.iceGatheringState === 'complete') {
            resolve('');
          } else {
            const checkState = () => {
              if (pcRef.current?.iceGatheringState === 'complete') {
                pcRef.current.removeEventListener('icegatheringstatechange', checkState);
                resolve('');
              }
            };
            pcRef.current.addEventListener('connectionstatechange', onConnectionstatechange);
            pcRef.current.addEventListener('icegatheringstatechange', checkState);
          }
        });
      })
      .then(async () => {
        const offer = pcRef.current.localDescription;
        const data = await socketInstance.current[
          rtcAPIType === 'order-service' ? 'openRtc' : 'openPositionAssistance'
        ]({
          offer: offer.sdp,
        });

        pcRef.current.setRemoteDescription({
          type: 'answer',
          sdp: data?.answer,
        });
      });
  }, [onConnectionstatechange, rtcAPIType, socketInstance]);

  const start = useCallback(
    async (onOk?: VoidFunc, onError?: VoidFunc) => {
      if (isStartedOpenRTC.current) {
        onOk?.();
        return;
      }

      isStartedOpenRTC.current = true;

      if (['new', 'connecting', 'connected'].includes(currentConnectState.current)) {
        onOk?.();
        return;
      }

      const config = {
        sdpSemantics: 'unified-plan',
        iceServers: [],
      };

      console.log('[WebRTC] 开启RTC！');
      pcRef.current = new RTCPeerConnection(config);
      pcRef.current.addTransceiver('video', { direction: 'recvonly' });
      pcRef.current.addEventListener('track', evt => {
        if (evt.track.kind === 'video') {
          console.log('[WebRTC] receive video track', evt);
          videoRef.current.srcObject = evt.streams[0];

          videoRef.current.addEventListener('play', onVideoPlayEvent);
        }
      });
      try {
        await createOffer();
        onOk?.();
      } catch (e) {
        isStartedOpenRTC.current = false;
        onError?.();
      }
    },
    [createOffer, onVideoPlayEvent, videoRef]
  );

  const stop = useCallback(
    async (cb?: VoidFunc) => {
      console.log('[WebRTC] 准备断开rtc，当前状态', currentConnectState.current);
      if (!currentConnectState.current) {
        isStartedOpenRTC.current = false;
        cb?.();
        return;
      }
      console.log('[WebRTC] 开始执行关闭RTC...');
      try {
        pcRef.current?.close();
        pcRef.current = null;
      } catch (e) {
        console.error('[WebRTC] 本地关闭RTC异常：', e);
      }
      try {
        // await socketInstance.current[rtcAPIType === 'order-service' ? 'closeRtc' : 'closePositionAssistance']();

        // 改用局域网接口，防止socket连接状态导致RTC未关闭的问题
        await service.lanInterface[rtcAPIType === 'order-service' ? 'closeRtc' : 'closePositionAssistance'](
          latestDeviceId.current
        );

        console.log('[WebRTC] 成功关闭RTC！');
        isStartedOpenRTC.current = false;
      } catch (e) {
        console.log(e);
      } finally {
        cb?.();
      }

      currentConnectState.current = null;
    },
    [latestDeviceId, rtcAPIType]
  );

  return {
    start,
    stop,
  };
};
